import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { get } from 'lodash';
import {
  saveSelectedKPI,
  getSelectedKPI,
  saveAndApplySelectedKPI,
  updateCustomKPI,
  deleteCustomKPI,
  getSPIThresholds,
  getKpiMetaConfigs,
} from '@src/api/SPI';
import kpiStore from '@src/globalStore/kpiStore';
import { isAppSPIEnabledSelector } from '@store/common/selectors';
import { isAdModule, isEiModule, isAppModule, isEcoServerModule, isOdpModule } from '@src/utils/constants';
import { checkFeature } from '@utils/platform/features/checkFeature';

// load and save selected kpi for user
// the selected kpi is prefetched and cached(on client and server) as part useInitialization
function useKPI() {
  const setCurrentKpi = kpiStore((state) => state.setCurrentKpi);
  const setKPIs = kpiStore((state) => state.setKPIs);
  const setKpiMetaConfigs = kpiStore((state) => state.setKpiMetaConfigs);
  const queryClient = useQueryClient();
  const isAppSPIEnabled = useSelector(isAppSPIEnabledSelector);

  let kpiEnabled = false;
  if (isAdModule || isOdpModule || isEcoServerModule) {
    kpiEnabled = false;
  } else if (isAppModule) {
    kpiEnabled = isAppSPIEnabled;
  } else if (isEiModule) {
    kpiEnabled = checkFeature('common.kpi', true);
  }

  const { data: kpi, isLoading: isKpiLoading } = useQuery(
    ['selected-kpi'],
    () => getSelectedKPI({}),
    {
      retry: true,
      enabled: kpiEnabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 min
      select: (response) => get(response, ['data'], {}),
    },
  );

  const { data: kpiList, isLoading: isKpiListLoading } = useQuery(
    ['kpi-list'],
    () => getSPIThresholds({}),
    {
      retry: true,
      enabled: kpiEnabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 min
      select: (response) => get(response, ['data'], {}),
    },
  );

  const { data: kpiMetaConfigs } = useQuery(['kpi-meta-configs'], () => getKpiMetaConfigs({}), {
    retry: true,
    enabled: kpiEnabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 min
    select: (response) => get(response, ['data'], {}),
  });

  const forceUpdateCurrentKpi = () => {
    queryClient.invalidateQueries('selected-kpi');
  };

  const forceUpdateKpiList = () => {
    queryClient.invalidateQueries('kpi-list');
  };

  const forceUpdateKpiMetaConfigs = () => {
    queryClient.invalidateQueries('kpi-meta-configs');
  };

  useEffect(() => {
    setCurrentKpi(kpi);
  }, [kpi]);

  useEffect(() => {
    if (kpiList && kpiList.kpiThresholds && kpiList.kpiThresholds.length > 0) {
      setKPIs(kpiList.kpiThresholds);
    }
  }, [kpiList, setKPIs]);

  useEffect(() => {
    if (kpiMetaConfigs) {
      setKpiMetaConfigs(kpiMetaConfigs);
    }
  }, [kpiMetaConfigs, setKpiMetaConfigs]);

  // save user selection, invalidate cache on success
  const applyKpiMutation = useMutation(
    (kpiId: number) => {
      return saveSelectedKPI({ kpiId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('selected-kpi');
      },
    },
  );

  const saveKpiThresholdsMutation = useMutation(
    (thresholds) => {
      return saveAndApplySelectedKPI(thresholds);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kpi-list');
        queryClient.invalidateQueries('selected-kpi');
      },
    },
  );

  const updateKpiThresholdsMutation = useMutation(
    (thresholds) => {
      return updateCustomKPI(thresholds);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kpi-list');
        queryClient.invalidateQueries('selected-kpi');
      },
    },
  );

  const deleteKpiMutation = useMutation(
    (kpiId) => {
      return deleteCustomKPI({ kpiId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kpi-list');
        queryClient.invalidateQueries('selected-kpi');
      },
    },
  );

  return {
    applyKpiMutation,
    saveKpiThresholdsMutation,
    updateKpiThresholdsMutation,
    deleteKpiMutation,
    isKpiLoading,
    isKpiListLoading,
    forceUpdateCurrentKpi,
    forceUpdateKpiList,
    forceUpdateKpiMetaConfigs,
  };
}

export default useKPI;
